import Spinner from "components/Spinner";
import Dropdown from "react-bootstrap/Dropdown";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

import PendingOrders from "./Pending/PendingOrders";
import ProcessingOrders from "./Processing/ProcessingOrders";
import AllOrders from "./All/AllOrders";
import InvoiceReadyOrders from "./InvoiceReady/InvoiceReadyOrders";
import PendingInvoicesTabs from "components/PendingInvoicesTabs";
import { useWoStatuses, woStatusLabel } from "utils/woStatus";

const DUE_PRESETS = [
  { value: "all", label: "All" },
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "overdue", label: "Overdue" },
];

/**
 * A Date as YYYY-MM-DD in the VIEWER's timezone.
 *
 * Not toISOString().slice(0, 10) -- that converts to UTC first, so for anyone
 * west of Greenwich (which is every user of this app) an evening "today" is
 * reported as tomorrow, and the Today filter quietly shows the wrong day.
 */
const toDateKey = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Resolve a quick filter to concrete due-date bounds.
 *
 * Done here rather than on the server because "this week" is a question about
 * the viewer's calendar, and the API server's clock is not it. The API only
 * ever sees two plain dates.
 */
const dueDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (preset) {
    case "today":
      return { from: toDateKey(today), to: toDateKey(today) };

    case "week": {
      // Sunday..Saturday, matching how the rest of the app reads a week.
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { from: toDateKey(start), to: toDateKey(end) };
    }

    case "month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      // Day 0 of next month is the last day of this one, leap years included.
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: toDateKey(start), to: toDateKey(end) };
    }

    case "overdue": {
      // Everything due before today. Open ended at the start: an order six
      // months late is still overdue.
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { from: "", to: toDateKey(yesterday) };
    }

    default:
      return { from: "", to: "" };
  }
};

// invoiceOnly: the Job Board tab of the Pending Invoices page (user,
// 2026-10-29) -- the old Job Board "Pending Invoices" view, no status tabs.
// Its key is "__invoice": a status value is only [a-z0-9-], so no status
// added in Settings can collide with it.
export default function JobBoard({ invoiceOnly = false }) {
  // Url search params
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTextRef = useRef("");

  /*   All States
   ********************************************* */
  // Opens on Approved (user, 2026-11-03; was Processing).
  const [active, setActive] = useState(invoiceOnly ? "__invoice" : "approved");
  // Tabs = All, then Settings -> WO Statuses in its order (user, 2026-10-30:
  // All first everywhere). Each tab lists the orders with that status; Ready,
  // Ready for Pickup and Pending Invoices tabs are gone (Ready for Pickup is a
  // status now).
  const woStatuses = useWoStatuses();
  const tabs = [
    { value: "all", label: "All" },
    ...woStatuses.map((s) => ({ value: s.value, label: s.name })),
  ];
  useEffect(() => {
    if (!invoiceOnly && woStatuses.length && !tabs.some((t) => t.value === active)) {
      setActive(woStatuses.some((s) => s.value === "approved") ? "approved" : woStatuses[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woStatuses]);

  const [searchText, setSearchText] = useState(null);

  const [loading, setLoading] = useState(false);

  const [locationsData, setLocationsData] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [productTypesData, setProductTypesData] = useState(false);
  const [productTypes, setProductTypes] = useState(false);

  const [refreshStatusList, setRefreshStatusList] = useState({});

  // Due-date filter. The preset drives the range except under "custom", where
  // the two inputs do. Held apart so switching to Today and back to Custom does
  // not wipe the dates that were typed in.
  const [datePreset, setDatePreset] = useState("all");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });

  const dateRange =
    datePreset === "custom" ? customRange : dueDateRange(datePreset);

  // What the toolbar button says. Custom shows the dates themselves rather than
  // the word "Custom", so the active filter is readable without opening it.
  const dueLabel =
    datePreset === "custom"
      ? [customRange.from || "any", customRange.to || "any"].join(" - ")
      : DUE_PRESETS.find((p) => p.value === datePreset)?.label || "All";

  /*   All Functions
   ********************************************* */

  const getAllLocations = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/locations", { headers: authHeader() });

      setLocationsData(data);

      setLoading(false);
    } catch (err) {
      alert(err.message);
      setLoading(false);
    }
  };

  const getAllProductTypes = async () => {
    try {
      const { data } = await API.get("/productTypes", {
        headers: authHeader(),
      });
      // Setting This Product Types Data for dropdown and changing productTypes in quick view

      // filter out types where showOnJobBoard is false
      const filteredData = data.filter((item) => item.showOnJobBoard !== false);

      setProductTypesData(filteredData);

      let refreshList = {};

      let productTypeList = filteredData?.map((item) => item?.productTypes);

      // let updatedProductTypeList = [...productTypeList];
      // console.log(updatedProductTypeList);

      // Getting Refresh List From Possible Product Types.
      productTypeList?.map((item) => {
        refreshList[item] = false;
      });

      setRefreshStatusList(refreshList);

      // Setting This to loop over Product Wise Card
      setProductTypes(filteredData);
    } catch (err) {
      alert(err.message);
      setLoading(false);
    }
  };

  const refreshAll = () => {
    setRefreshStatusList((prev) => {
      let newList = {};
      Object.keys(prev)?.map((item) => {
        newList[item] = true;
      });

      return newList;
    });
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllLocations();
      getAllProductTypes();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const locationId = searchParams.get("location");

    if (locationId) {
      setSelectedLocation(parseInt(locationId));
    }
  }, [searchParams]);

  return (
    <div
      className=""
      // Pending Invoices: line the title, tabs and cards up with its W Orders
      // tab (title at 272px, list at 284px) so switching tabs does not jump.
      style={{ paddingLeft: invoiceOnly ? 284 : 280, paddingTop: 20, height: "100vh" }}
    >
      {loading && <Spinner />}
      <div
        className="nav nav-tabs nav-bordered mb-3 bg-white shadow-sm py-2 d-flex justify-content-between flex-grow-1"
        style={{
          position: "fixed",
          top: 0,
          width: "100vw",
          left: 0,
          paddingLeft: invoiceOnly ? 272 : 280,
          zIndex: 101,
        }}
      >
        {invoiceOnly ? (
          <PendingInvoicesTabs active="job-board" />
        ) : (
        <div className="d-flex">
          {tabs.map(({ value, label }) => (
            <div key={value} onClick={() => setActive(value)}>
              <button
                className={
                  active === value ? `btn active bg-primary text-white` : "btn"
                }
              >
                <span className="d-none d-md-block">{label}</span>
              </button>
            </div>
          ))}
        </div>
        )}
        <div className="d-flex align-items-center gap-4">
          {/*
            One button, not a row of controls. This toolbar is position: fixed
            while the board below it clears a FIXED amount of space, so anything
            that makes the toolbar wrap onto a second line puts it on top of the
            cards. The custom range in particular is two date inputs wide, which
            wraps even on a large screen -- so the presets and the range both
            live inside the menu and the toolbar only ever spends one button on
            this.
          */}
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="info">
              Due: {dueLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu
              // The toolbar is a stacking/overflow context; without a fixed
              // strategy the menu is clipped by it.
              popperConfig={{ strategy: "fixed" }}
              // Put the menu in the DOM at mount instead of on first open.
              // Measured without it: first open landed at left 0, behind the
              // sidebar; the second and every one after at the button (924).
              renderOnMount
              // Popper positions against an element it has measured, and on the
              // very first open there is nothing to measure yet, so it placed
              // the menu at the far left of the window -- behind the sidebar.
              // Every later open looked right because by then it had been
              // measured once. This makes the first open the same as the rest.
              style={{ minWidth: 250 }}
            >
              {DUE_PRESETS.map((preset) => (
                <Dropdown.Item
                  key={preset.value}
                  active={datePreset === preset.value}
                  onClick={() => setDatePreset(preset.value)}
                >
                  {preset.label}
                </Dropdown.Item>
              ))}

              <Dropdown.Divider />

              <div className="px-3 pb-2">
                <div className="small text-muted mb-1">Custom range</div>
                <div className="d-flex align-items-center gap-1">
                  <input
                    type="date"
                    aria-label="Due from"
                    className="form-control form-control-sm"
                    value={customRange.from}
                    onChange={(e) => {
                      setCustomRange((prev) => ({
                        ...prev,
                        from: e.target.value,
                      }));
                      // Typing a date IS choosing custom -- making the user
                      // pick "Custom" first would leave dates on screen that
                      // are not filtering anything.
                      setDatePreset("custom");
                    }}
                  />
                  <span className="text-muted small">to</span>
                  <input
                    type="date"
                    aria-label="Due to"
                    className="form-control form-control-sm"
                    value={customRange.to}
                    onChange={(e) => {
                      setCustomRange((prev) => ({
                        ...prev,
                        to: e.target.value,
                      }));
                      setDatePreset("custom");
                    }}
                  />
                </div>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          <button onClick={refreshAll} className="btn btn-success">
            Refresh All
          </button>

          <div className="d-flex align-items-center gap-2 text-end">
            <h4>Brand:</h4>
            <select
              className="form-control form-control-sm"
              value={selectedLocation || ""}
              onChange={(e) => {
                if (e.target.value === "") {
                  setSelectedLocation(null);
                } else {
                  setSelectedLocation(parseInt(e.target.value));
                }
              }}
            >
              <option value={""}> All </option>
              {locationsData?.map((item) => (
                <option key={item?.id} value={item?.id}>
                  {item?.locationName}
                </option>
              ))}
            </select>
          </div>
          <div className="text-end me-3">
            <div className="d-flex justify-content-center align-items-center">
              <form
                className="d-flex align-items-center gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSearchText(searchTextRef.current.value);
                }}
              >
                <input
                  type="search"
                  ref={searchTextRef}
                  placeholder="Search..."
                  // defaultValue={searchTextRef.current.value}
                  // onChange={(e) => setSearchText(e.target.value)}
                  className="form-control"
                />
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
                {searchTextRef.current.value && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      searchTextRef.current.value = "";
                      setSearchText("");
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Processing keeps its own list: it hides products already marked
          Ready (same kept rule as the W Orders Processing tab). */}
      {active === "processing" && (
        <div className="">
          {/* Below the fixed tab bar; ProcessingOrders brings its own mt-5. */}
          <div className="alert alert-info py-1 px-2 mb-n4 small" style={{ marginTop: 96 }}>
            <i className="mdi mdi-information-outline me-1" />
            This tab hides {woStatusLabel(woStatuses, "processing")} orders whose products are
            all marked Ready. They still show under All.
          </div>
          <ProcessingOrders
          selectedLocation={selectedLocation}
          searchText={searchText}
          productTypesData={productTypesData}
          refreshStatusList={refreshStatusList}
          setRefreshStatusList={setRefreshStatusList}
          dateFrom={dateRange.from}
          dateTo={dateRange.to}
          />
        </div>
      )}
      {active === "all" && (
        <AllOrders
          selectedLocation={selectedLocation}
          searchText={searchText}
          productTypesData={productTypesData}
          refreshStatusList={refreshStatusList}
          setRefreshStatusList={setRefreshStatusList}
          dateFrom={dateRange.from}
          dateTo={dateRange.to}
        />
      )}
      {active === "__invoice" && (
        <InvoiceReadyOrders
          selectedLocation={selectedLocation}
          searchText={searchText}
          productTypesData={productTypesData}
          refreshStatusList={refreshStatusList}
          setRefreshStatusList={setRefreshStatusList}
          dateFrom={dateRange.from}
          dateTo={dateRange.to}
        />
      )}
      {!["processing", "all", "__invoice"].includes(active) && (
        <PendingOrders
          key={active}
          woStatus={active}
          selectedLocation={selectedLocation}
          searchText={searchText}
          productTypesData={productTypesData}
          refreshStatusList={refreshStatusList}
          setRefreshStatusList={setRefreshStatusList}
          dateFrom={dateRange.from}
          dateTo={dateRange.to}
        />
      )}
    </div>
  );
}
